import { Translation } from '@ps/web-shared-components';
import type { ReactElement } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';

import Spinner from '@swf/components/Loaders/Spinner/Spinner';
import { getAvailableMultipleOpportunities } from '@swf/core/models/sports/betSlip/utils/getAvailableOpportunities';
import {
  getIsAvailableOpportunity,
  getIsSgmMultipleOpportunity,
  getIsSgmOpportunity
} from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import { select } from '@swf/core/store';
import { Odds } from '@swf/sports/components/Odds/Odds';
import { getMultipleName } from '@swf/sports/utils/getMultipleName';

import useBetslipHasItemWithoutOdds from '../../../../hooks/useBetslipHasItemWithoutOdds';

import { TXTWCL_Accumulator, TXTWCL_Bet_Builder } from './BetSlipHeaderDetails.messages.json';
import styles from './BetSlipHeaderDetails.scss';

const BetSlipHeaderDetails: React.FC = () => {
  const multiples = useSelector(select.betSlip.multipleOpportunities);
  const bettableMultiples = getAvailableMultipleOpportunities(multiples);
  const singles = useSelector(select.betSlip.singleOpportunities);
  const pendingSelectionCount = useSelector(select.betSlip.pendingSelectionCount);
  const pendingRemovalCount = useSelector(select.betSlip.pendingRemovalCount);
  const singlesCount = singles.length;
  const betslipHasItemWithoutOdds = useBetslipHasItemWithoutOdds();

  if (pendingSelectionCount || pendingRemovalCount) {
    return (
      <span className={styles.spinner}>
        <Spinner />
      </span>
    );
  }

  if (betslipHasItemWithoutOdds) {
    return null;
  }

  if (singlesCount === 1) {
    const [single] = singles;

    if (getIsAvailableOpportunity(single)) {
      if (single.winRunnerOdds) {
        return (
          <>
            <span className={styles.dot} /> <Odds {...single.winRunnerOdds} />
          </>
        );
      }
      return null;
    }
  }

  const singleLineMultiples = bettableMultiples.filter(
    (opportunity) => opportunity.numberOfLines === 1
  );
  const hasInvalidSgm = multiples.some(
    (opportunity) => getIsSgmOpportunity(opportunity) && !getIsAvailableOpportunity(opportunity)
  );
  const availableSgmCount = multiples.filter(
    (opportunity) => getIsSgmOpportunity(opportunity) && getIsAvailableOpportunity(opportunity)
  ).length;
  const sgmMultiples = multiples.filter(
    (opportunity) =>
      getIsSgmMultipleOpportunity(opportunity) && getIsAvailableOpportunity(opportunity)
  );

  if (
    (singleLineMultiples.length !== 1 || availableSgmCount > 1 || hasInvalidSgm) &&
    sgmMultiples.length !== 1
  ) {
    return null;
  }

  const [singleLineMultiple] = singleLineMultiples;
  let multipleName: ReactElement | '';

  if (availableSgmCount === 1 || sgmMultiples.length === 1) {
    multipleName = <Translation {...TXTWCL_Bet_Builder} />;
  } else if (singlesCount === 2 || singlesCount === 3) {
    multipleName = getMultipleName({
      betType: singleLineMultiple.type,
      numberOfLines: singleLineMultiple.numberOfLines
    });
  } else {
    multipleName = <Translation {...TXTWCL_Accumulator} />;
  }

  return (
    <>
      <span className={styles.dot} />
      <span className={styles.ellipsis}>{multipleName}</span>
      <span className={styles.odds}>
        &nbsp;
        <Odds {...singleLineMultiple.winRunnerOdds} />
      </span>
    </>
  );
};

export default BetSlipHeaderDetails;
